// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import {
  FaUsers,
  FaNewspaper,
  FaCog,
  FaChartBar,
  FaComments,
  FaFileAlt,
  FaChartLine,
  FaSignOutAlt,
  FaBriefcase,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AnalyticsDashboard from './AnalyticsDashboard';
import ManageNews from './ManageNews';
import ManageFeedback from './ManageFeedback';
// import ManagePages from './ManagePages'; // removed
import ManageCareers from './ManageCareers';
import ManageApplications from './ManageApplications';
import ManageUsers from './ManageUsers';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState({
    news: 0,
    feedback: 0,
    users: 0,
    careers: 0,
    applications: 0,
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchRecentActivity();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          news: data.news || 0,
          feedback: data.feedback || 0,
          users: data.users || 0,
          careers: data.careers || 0,
          applications: data.applications || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getCreatedAt = obj =>
    obj?.createdAt || obj?.created_at || obj?.created_on || null;

  const fetchRecentActivity = async () => {
    try {
      const token = localStorage.getItem('token');
      setLoadingActivity(true);

      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // latest news
      const newsRes = await fetch(`${API_URL}/api/news?limit=1`, { headers });
      const newsJson = await newsRes.json();
      const newsList = Array.isArray(newsJson.news)
        ? newsJson.news
        : Array.isArray(newsJson)
        ? newsJson
        : [];
      const latestNews = newsList[0];

      // latest feedback
      const feedbackRes = await fetch(`${API_URL}/api/feedback?limit=1`, {
        headers,
      });
      const feedbackJson = await feedbackRes.json();
      const feedbackList = Array.isArray(feedbackJson.feedback)
        ? feedbackJson.feedback
        : Array.isArray(feedbackJson)
        ? feedbackJson
        : [];
      const latestFeedback = feedbackList[0];

      // latest user
      const usersRes = await fetch(
        `${API_URL}/api/admin/users?limit=1&sort=createdAt:desc`,
        { headers }
      );
      const usersJson = await usersRes.json();
      const usersList = Array.isArray(usersJson.users)
        ? usersJson.users
        : [];
      const latestUser = usersList[0];

      // latest career
      const careersRes = await fetch(`${API_URL}/api/careers?limit=1`);
      const careersJson = await careersRes.json();
      const careersList = Array.isArray(careersJson.careers)
        ? careersJson.careers
        : Array.isArray(careersJson)
        ? careersJson
        : [];
      const latestCareer = careersList[0];

      const activities = [];

      if (latestNews) {
        activities.push({
          type: 'news',
          title: 'New news article published',
          subtitle: latestNews.title,
          timestamp: new Date(getCreatedAt(latestNews)),
          icon: <FaNewspaper className="text-blue-600 dark:text-blue-400" />,
          bgColor: 'bg-blue-100 dark:bg-blue-900',
        });
      }

      if (latestFeedback) {
        activities.push({
          type: 'feedback',
          title: 'New feedback received',
          subtitle: `${latestFeedback.name || 'Anonymous'} - ${
            latestFeedback.rating ?? '-'
          }★`,
          timestamp: new Date(getCreatedAt(latestFeedback)),
          icon: <FaComments className="text-green-600 dark:text-green-400" />,
          bgColor: 'bg-green-100 dark:bg-green-900',
        });
      }

      if (latestUser) {
        activities.push({
          type: 'user',
          title: 'New user registered',
          subtitle: latestUser.name || latestUser.email,
          timestamp: new Date(getCreatedAt(latestUser)),
          icon: <FaUsers className="text-purple-600 dark:text-purple-400" />,
          bgColor: 'bg-purple-100 dark:bg-purple-900',
        });
      }

      if (latestCareer) {
        activities.push({
          type: 'career',
          title: 'New job opening posted',
          subtitle: latestCareer.title,
          timestamp: new Date(getCreatedAt(latestCareer)),
          icon: (
            <FaBriefcase className="text-orange-600 dark:text-orange-400" />
          ),
          bgColor: 'bg-orange-100 dark:bg-orange-900',
        });
      }

      activities.sort((a, b) => b.timestamp - a.timestamp);
      setRecentActivity(activities.slice(0, 4));
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  const getTimeAgo = timestamp => {
    if (!timestamp) return '';
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: <FaChartBar /> },
    { id: 'analytics', label: 'Analytics', icon: <FaChartLine /> },
    { id: 'users', label: 'Users', icon: <FaUsers /> },
    { id: 'careers', label: 'Careers', icon: <FaBriefcase /> },
    { id: 'applications', label: 'Applications', icon: <FaFileAlt /> },
    { id: 'news', label: 'Manage News', icon: <FaNewspaper /> },
    { id: 'feedback', label: 'Manage Feedback', icon: <FaComments /> },
    // pages removed
    { id: 'account', label: 'Account Settings', icon: <FaCog /> },
  ];

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate('/admin/login');
    }
  };

  const handlePasswordChange = e => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async e => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordMessage({ type: '', text: '' });

    if (
      !passwordForm.currentPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      setPasswordMessage({ type: 'error', text: 'All fields are required' });
      setPasswordSaving(false);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      setPasswordSaving(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage({
        type: 'error',
        text: 'New password must be at least 6 characters',
      });
      setPasswordSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPasswordMessage({
          type: 'error',
          text: 'Not authenticated. Please log in again.',
        });
        setPasswordSaving(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordMessage({
          type: 'success',
          text: 'Password updated successfully! Use the new password on next login.',
        });
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        setPasswordMessage({
          type: 'error',
          text: data.error || 'Failed to update password',
        });
      }
    } catch (err) {
      console.error('Admin password update error:', err);
      setPasswordMessage({
        type: 'error',
        text: 'Network error. Please try again.',
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white dark:bg-gray-800 shadow-lg min-h-screen fixed left-0 top-0 bottom-0 overflow-y-auto">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">N</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Admin Panel
                </h2>
              </div>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              NEVXOAULIO VENTURES
            </p>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <FaUsers className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {user?.name || 'Admin'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>

          <nav className="mt-2">
            {menuItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-all duration-200 ${
                  activeSection === item.id
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-6 py-3 text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 mt-4"
            >
              <FaSignOutAlt className="text-lg" />
              <span className="font-medium">Logout</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 ml-64 p-8">
          {activeSection === 'overview' && (
            <div>
              <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                  Dashboard Overview
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Welcome back, {user?.name}! Here's what's happening today.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <FaNewspaper className="text-4xl opacity-80" />
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      Active
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-1 opacity-90">
                    News Articles
                  </h3>
                  <p className="text-4xl font-bold">{stats.news || 0}</p>
                </div>

                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <FaComments className="text-4xl opacity-80" />
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      Live
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-1 opacity-90">
                    Feedback
                  </h3>
                  <p className="text-4xl font-bold">{stats.feedback || 0}</p>
                </div>

                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <FaUsers className="text-4xl opacity-80" />
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      Total
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-1 opacity-90">
                    Users
                  </h3>
                  <p className="text-4xl font-bold">{stats.users || 0}</p>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <FaBriefcase className="text-4xl opacity-80" />
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      Open
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-1 opacity-90">
                    Job Openings
                  </h3>
                  <p className="text-4xl font-bold">{stats.careers || 0}</p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Quick Actions
                </h2>
                <div className="grid md:grid-cols-3 gap-4">
                  <button
                    onClick={() => setActiveSection('news')}
                    className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-left"
                  >
                    <FaNewspaper className="text-2xl text-blue-600 dark:text-blue-400 mb-2" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Add News Article
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Publish latest updates
                    </p>
                  </button>

                  <button
                    onClick={() => setActiveSection('careers')}
                    className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors text-left"
                  >
                    <FaBriefcase className="text-2xl text-green-600 dark:text-green-400 mb-2" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Post Job Opening
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Add new career opportunity
                    </p>
                  </button>

                  <button
                    onClick={() => setActiveSection('analytics')}
                    className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors text-left"
                  >
                    <FaChartLine className="text-2xl text-purple-600 dark:text-purple-400 mb-2" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      View Analytics
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Check site performance
                    </p>
                  </button>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="mt-8 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Recent Activity
                </h2>

                {loadingActivity ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Loading recent activity...
                  </div>
                ) : recentActivity.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No recent activity yet
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recentActivity.map((activity, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div
                          className={`w-10 h-10 ${activity.bgColor} rounded-full flex items-center justify-center`}
                        >
                          {activity.icon}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                            {activity.subtitle}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {getTimeAgo(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'analytics' && <AnalyticsDashboard />}
          {activeSection === 'news' && <ManageNews />}
          {activeSection === 'feedback' && <ManageFeedback />}
          {activeSection === 'users' && <ManageUsers />}
          {activeSection === 'careers' && <ManageCareers />}
          {activeSection === 'applications' && <ManageApplications />}

          {activeSection === 'account' && (
            <div className="max-w-xl">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Account Settings
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Change your admin password. You will use the new password on next
                login.
              </p>

              <form
                onSubmit={handlePasswordSubmit}
                className="space-y-4 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700"
              >
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                    placeholder="Enter current password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                    placeholder="New password (min 6 characters)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                    placeholder="Re-enter new password"
                  />
                </div>

                {passwordMessage.text && (
                  <div
                    className={`px-4 py-3 rounded-lg text-sm font-semibold ${
                      passwordMessage.type === 'success'
                        ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}
                  >
                    {passwordMessage.text}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={passwordSaving}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {passwordSaving ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
